import { useContext, useEffect } from 'react';
import { TenantContext, tenant as activeTenant } from './tenantContextValue';

// ── CSS variable injection ──────────────────────────────────────────────────
function applyTenantColors(colors) {
  const root = document.documentElement;
  const map = {
    accent: '--accent',
    accentDark: '--accent-dark',
    accentLight: '--accent-light',
    accentSubtle: '--accent-subtle',
    accentGlow: '--accent-glow',
    sidebarBg: '--sidebar-bg',
    sidebarBorder: '--sidebar-border',
    sidebarText: '--sidebar-text',
    sidebarMuted: '--sidebar-muted',
  };
  Object.entries(map).forEach(([key, cssVar]) => {
    if (colors[key]) root.style.setProperty(cssVar, colors[key]);
  });
}

export function TenantProvider({ children }) {
  useEffect(() => {
    applyTenantColors(activeTenant.colors);
    document.title = `${activeTenant.name} - ${activeTenant.fullTagline}`;
  }, []);

  return (
    <TenantContext.Provider value={activeTenant}>
      {children}
    </TenantContext.Provider>
  );
}

import bundledLogo from '../assets/logo-pdf.png';

// Option 3 (per-user config, 2025-11): every tenant record's
// logoUrl / iconUrl is ignored and the app always renders the
// bundled src/assets/logo-pdf.png. Removes the CORS spam from
// the old FreedomSoft S3 URL and gives every place a consistent
// brand mark. If a tenant-specific logo is wanted later, flip
// these back to reading t.logoUrl / t.iconUrl first.
//
// The bundled PNG is 400×138 and includes a "PARACLETE OIL"
// wordmark in the bottom band. The icon mark itself occupies
// x=73..325, y=18..97 (a 253×80 region). We render the full
// image inside an overflow:hidden frame sized + offset to that
// region so only the icon shows — no text — and `size` is the
// icon's display height.
const ICON_SRC_W = 400;
const ICON_SRC_H = 138;
const ICON_CROP_X = 73;
const ICON_CROP_Y = 18;
const ICON_CROP_W = 253;
const ICON_CROP_H = 80;

// Inner image position + size are percentage-based so the crop stays
// correct even when external CSS constrains the wrapper (e.g. the
// login page applies `.logo-icon { max-height: 32px }` and
// `.auth-logo img { width: 100% }` regardless of the `size` prop).
const INNER_IMG_STYLE = {
  position: 'absolute',
  width: `${(ICON_SRC_W / ICON_CROP_W) * 100}%`,
  height: `${(ICON_SRC_H / ICON_CROP_H) * 100}%`,
  left: `${(-ICON_CROP_X / ICON_CROP_W) * 100}%`,
  top: `${(-ICON_CROP_Y / ICON_CROP_H) * 100}%`,
  maxWidth: 'none',
  maxHeight: 'none',
  display: 'block',
};

function CroppedLogo({ size, className }) {
  return (
    <span
      className={className}
      style={{
        display: 'inline-block',
        position: 'relative',
        height: size,
        aspectRatio: `${ICON_CROP_W} / ${ICON_CROP_H}`,
        overflow: 'hidden',
        flexShrink: 0,
        verticalAlign: 'middle',
      }}
    >
      <img src={bundledLogo} alt="Logo" style={INNER_IMG_STYLE} />
    </span>
  );
}

function isCustomTenant(currentTenant) {
  return Boolean(currentTenant?.id && currentTenant.id !== 'shadowland');
}

function TenantTextIcon({ tenant, size, className }) {
  return (
    <span
      className={className}
      aria-label={`${tenant?.name || 'Tenant'} icon`}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: size,
        width: size,
        flexShrink: 0,
        border: `1px solid ${tenant?.colors?.accentLight || 'rgba(255,255,255,0.35)'}`,
        background: tenant?.logoBackground || tenant?.colors?.sidebarBg || '#0f172a',
        color: tenant?.colors?.sidebarText || '#ffffff',
        fontSize: Math.max(10, Math.round(size * 0.42)),
        fontWeight: 700,
        letterSpacing: 0,
        lineHeight: 1,
        verticalAlign: 'middle',
      }}
    >
      {tenant?.logoText || tenant?.name?.slice(0, 2) || 'OK'}
    </span>
  );
}

function TenantWordmark({ tenant, size, className }) {
  return (
    <span
      className={className}
      aria-label={`${tenant?.name || 'Tenant'} logo`}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: size,
        width: '100%',
        maxWidth: 340,
        padding: `${Math.max(7, Math.round(size * 0.12))}px ${Math.max(12, Math.round(size * 0.2))}px`,
        border: `1px solid ${tenant?.colors?.accentLight || 'rgba(255,255,255,0.25)'}`,
        background: tenant?.logoBackground || tenant?.colors?.sidebarBg || '#0f172a',
        color: tenant?.colors?.sidebarText || '#ffffff',
        fontSize: Math.max(18, Math.round(size * 0.36)),
        fontWeight: 700,
        letterSpacing: 0,
        lineHeight: 1,
        textAlign: 'center',
      }}
    >
      {tenant?.wordmarkText || tenant?.name || 'Tenant'}
    </span>
  );
}

function tenantLocalLogoUrl(currentTenant, kind) {
  if (!isCustomTenant(currentTenant)) return '';
  const candidate = kind === 'icon'
    ? currentTenant.iconUrl || currentTenant.logoUrl
    : currentTenant.logoUrl || currentTenant.iconUrl;
  return String(candidate || '').startsWith('/') ? candidate : '';
}

function TenantLogoIcon({ size, className }) {
  const currentTenant = useContext(TenantContext);
  const iconUrl = tenantLocalLogoUrl(currentTenant, 'icon');
  if (iconUrl) {
    return (
      <span
        className={className}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: size,
          width: size,
          overflow: 'hidden',
          flexShrink: 0,
          background: currentTenant?.logoBackground || '#ffffff',
          border: `1px solid ${currentTenant?.colors?.accent || 'rgba(0,0,0,0.15)'}`,
          verticalAlign: 'middle',
        }}
      >
        <img
          src={iconUrl}
          alt={`${currentTenant?.name || 'Tenant'} icon`}
          style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
        />
      </span>
    );
  }
  if (isCustomTenant(currentTenant) && currentTenant?.logoText) {
    return <TenantTextIcon tenant={currentTenant} size={size} className={className} />;
  }
  return <CroppedLogo size={size} className={className} />;
}

export const LogoIcon = ({ size = 28, className = 'logo-icon' }) => (
  <TenantLogoIcon size={size} className={className} />
);

export const LogoIconSmall = ({ size = 28, className = 'logo-icon' }) => (
  <TenantLogoIcon size={size} className={className} />
);

// Full uncropped logo (icon + "PARACLETE OIL" wordmark below).
// No `logo-icon` class so the login page isn't capped by its
// max-height: 32px rule — `size` is honored directly.
export const LogoFull = ({ size = 64, className = 'logo-full' }) => {
  const currentTenant = useContext(TenantContext);
  const logoUrl = tenantLocalLogoUrl(currentTenant, 'logo');
  if (logoUrl) {
    return (
      <img
        src={logoUrl}
        alt={`${currentTenant?.name || 'Tenant'} logo`}
        className={className}
        style={{
          height: size,
          width: 'auto',
          maxWidth: '100%',
          objectFit: 'contain',
          display: 'block',
          margin: '0 auto',
        }}
      />
    );
  }
  if (isCustomTenant(currentTenant) && currentTenant?.wordmarkText) {
    return <TenantWordmark tenant={currentTenant} size={size} className={className} />;
  }
  return (
    <img
      src={bundledLogo}
      alt="Logo"
      className={className}
      style={{
        height: size,
        width: 'auto',
        maxWidth: '100%',
        objectFit: 'contain',
        display: 'block',
        margin: '0 auto',
      }}
    />
  );
};
